import { PaymentMethod } from '../../components/offer-checkout/constants/payment-method.enum';
import { BillingTerm } from '../../components/products/constants/billing-term.enum';
import { SelfCheckoutMetaResponseDto } from '../../components/register/checkout/step2/types/response';
import { InternalUserListResponseDto } from './internal/users';
import { UserSelectableEntityResponseDto } from './user';
import { Paginated } from './utils';

export enum OnboardingStage {
  AddUsers = 'ADD_USERS',
  IntroductionVideo = 'INTRODUCTION_VIDEO',
  Done = 'DONE'
}

export enum BanReason {
  None = 'NONE',
  PaymentOverdue = 'PAYMENT_OVERDUE',
  DeactivatedByAdmin = 'DEACTIVATED_BY_ADMIN'
}

export enum OrganizationStatusFilters {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  All = 'ALL'
}

// TODO - mocked at the moment
export enum FlatRate {
  CLASSIC = 'classic',
  PREMIUM = 'premium'
}

export interface SelfCheckoutListResponseDto {
  _id: string;
  mail: string;
  name: string;
  allowedJobsCount: number;
  newCustomerDate: string;
  createdAt: string;
}

export interface CapacityCreationEntitiesResponseDto {
  capacityBySlotsNeeded: CapacityBySlotsNeededResponseDto[];
}

export interface CapacityBySlotsNeededResponseDto {
  slotsNeeded: number;
  capacity: FutureCapacityResponseDto[];
}

export interface FutureCapacityResponseDto {
  date: string;
  freeCapacitySlots: number;
}

export interface CreateSelfCheckoutRequestDto {
  firstName: string;
  lastName: string;
  mail: string;
  name: string;
  allowedJobsCount: number;
  newCustomerDate: string;
  vatId: string;
  street: string;
  houseNumber: string;
  postCode: string;
  city: string;
  country: string;
  invoiceMail: string;
  iban: string;
  bic: string;
}

export interface SelfCheckoutDetailsResponseDto {
  _id: string;
  firstName: string;
  lastName: string;
  mail: string;
  name: string;
  newCustomerDate: string;
  vatId: string;
  street: string;
  houseNumber: string;
  postCode: string;
  city: string;
  country: string;
  invoiceMail: string;
  iban: string;
  bic: string;
  phone: string;
  ip: string;
  earlyOptOut: boolean;
  packageName: string;
  packagePrice: number;
  minimumContractDurationMonths: number;
  allowedJobsCount: number;
  trialStartAt?: string;
  createdAt: string;
  updatedAt: string;
  meta: SelfCheckoutMetaResponseDto;
  additionalConditions: string;
  additionalPriceConditions: string;
}

export interface SelfCheckoutCodeResponseDto {
  type: SelfCheckoutCodeType;
  valid: boolean;
}

export enum SelfCheckoutCodeType {
  TRIAL = 'TRIAL',
  START50 = 'START50',
  GOLD = 'GOLD',
  LITE = 'LITE',
  RECHNUNG = 'RECHNUNG'
}

export interface OrganizationInvitationListResponseDto {
  _id: string;
  firstName: string;
  lastName: string;
  mail: string;
  expiresAt: string;
  createdAt: string;
  name: string;
  userAlreadyCreated: boolean;
  isDeleteable: boolean;
}

/** One internal user assigned to an organization together with his share of the allowedJobsCount. */
export interface AssignedInternalUserResponseDto {
  belongsToUserId: string;
  fullName: string;
  /** Share of the organizations allowedJobsCount. 0 means notification only (representative), no provision. */
  capacity: number;
}

export interface ToggleMultipleAssignmentRequestDto {
  enabled: boolean;
}

export interface ChangeOrganizationAssignedInternalUsersRequestDto {
  assignedInternalUsers: { belongsToUserId: string; capacity: number }[];
}

export interface OrganizationListResponseDto {
  _id: string;
  name: string;
  customerNumber: string;
  belongsToStatus: string;
  statusDate: string;
  lastLogin: string;
  assignedInternalUserFullName: string;
  assignedRepresentativeUserFullName?: string;
  multipleAssignmentEnabled: boolean;
  assignedInternalUsers?: AssignedInternalUserResponseDto[];
  profilePictureUrl: string;
  isOpenable: boolean;
  rating: number;
  isMarkedAsFavorite: boolean;
  isTrialing?: boolean;
}

export interface OrganizationListPaginatedResponseDtoWithInvitationCountAndEntities {
  invitationCount: number;
  selfCheckoutCount: number;
  organizations: Paginated<OrganizationListResponseDto>;
  filterSelectableEntities: OrganizationFilterSelectableEntities;
  listEntities: OrganizationListEntityResponseDto;
}

export interface OrganizationFilterSelectableEntities {
  internalUsers: UserSelectableEntityResponseDto[];
  userFilterUsed: string;
  status: OrganizationStatusFilters[];
  statusFilterUsed: string;
}

export interface OrganizationListEntityResponseDto {
  status: OrganizationStatusSelectableOptionResponseDto[];
}

export interface OrganizationStatusSelectableOptionResponseDto {
  id: string;
  value: string;
  needsStatusDate: string;
  banReason: string;
  nextStatus: string;
  color: string;
  icon: string;
}

export interface OrganizationDetailsResponseDto {
  name: string;
  customerNumber: string;
  debitReference: string;
  signatureDate: string;
  vatId: string;
  street: string;
  houseNumber: string;
  postCode: string;
  city: string;
  country: string;
  invoiceMail: string;
  allowedJobsCount: number;
  onboardingStage: OnboardingStage;
  banReason: BanReason;
  belongsToStatus: string;
  statusDate: string;
  lastLogin: string;
  assignedInternalUserFullName: string;
  assignedRepresentativeUserFullName?: string;
  multipleAssignmentEnabled: boolean;
  assignedInternalUsers?: AssignedInternalUserResponseDto[];
  selectableEntities: OrganizationDetailsSelectableEntities;
  iban: string;
  bic: string;
  phone: string;
  organizationAdmin: InternalUserListResponseDto & { mail: string };
  organizationUsers: (InternalUserListResponseDto & { mail: string })[];
  selfCheckoutData?: SelfCheckoutOrganizationDetailsResponseDto;
  /** Only for organizations created by an accepted offer. */
  offerCheckoutData?: OfferCheckoutOrganizationDetailsResponseDto;
  /** While set, the organization is maintained via this accepted offer and the page is read only. */
  lockedByOfferId: string | null;
  futureCapacity?: number;
  futureCapacityDate?: string;
  isTrialing?: boolean;
  trialStartedAt?: string;
}

export interface SelfCheckoutOrganizationDetailsResponseDto {
  earlyOptOut: boolean;
  minimumContractDurationMonths: number;
  packageName: string;
  packagePrice: number;
  additionalConditions?: string | null;
  additionalPriceConditions?: string | null;
}

export interface OfferCheckoutOrganizationDetailsResponseDto {
  offerId: string;
  productTitle: string;
  productDescription: string;
  term: BillingTerm;
  termMonths: number;
  monthlyPriceCents: number;
  additionalConditions: string | null;
  addOns: { addOnId: string; title: string; description: string }[];
  paymentMethod: PaymentMethod;
  acceptedAt: string;
  ip: string;
  termsAccepted: boolean;
  sepaMandateAccepted: boolean;
  attachments: { attachmentId: string; title: string; fileName: string }[];
}

export interface UpdateOrganizationRequestDto {
  name: string;
  debitReference: string;
  signatureDate: string;
  vatId: string;
  street: string;
  houseNumber: string;
  postCode: string;
  city: string;
  country: string;
  invoiceMail: string;
  allowedJobsCount: number;
  iban: string;
  bic: string;
  phone: string;
}

export interface OrganizationDetailsSelectableEntities {
  status: OrganizationStatusSelectableOptionResponseDto[];
  internalUsers: UserSelectableEntityResponseDto[];
}
